/**
 * Flow selection. FR-202, FR-213.
 *
 * Lives in `shared/ui`, not in a feature, because both the calendar's quick-log
 * row and the day sheet (v0.2) need it — and features may not import each other
 * (`no-feature-cycles`). The boundary check caught this on the first run, which
 * is the argument for having it.
 *
 * This is tap 2 of the two-tap logging path, and the reason flow sits at the
 * top of the day sheet: it is the most-logged field by a wide margin.
 *
 * Saving is implicit — the control commits on change. There is no Save button
 * to forget, which matters most for the user this app is designed around: in
 * pain, in a hurry, or being watched (UI design Section 4).
 */
import { t } from '@copy/index'
// Type-only: a type import has no runtime edge, so this does not make a
// presentational primitive depend on domain logic. See the `ui-is-presentational`
// dependency-cruiser rule, which exempts type-only imports for exactly this reason.
import type { FlowLevel, ISODate } from '@domain/types'

/**
 * Duplicated from `@domain/types` rather than imported, because a value import
 * would give a UI primitive a runtime dependency on the domain layer. Four
 * strings are a cheaper price than that edge. The type import above keeps the
 * two in sync: adding a flow level to the domain breaks this line.
 */
const FLOW_LEVELS: readonly FlowLevel[] = ['none', 'spotting', 'light', 'medium', 'heavy']


function FlowIcon({ level, selected }: { level: FlowLevel; selected: boolean }) {
  const baseColor = selected
    ? 'currentColor'
    : {
        none: 'currentColor',
        spotting: 'var(--color-flow-spotting)',
        light: 'var(--color-flow-light)',
        medium: 'var(--color-flow-medium)',
        heavy: 'var(--color-flow-heavy)',
      }[level]

  if (level === 'none') {
    return (
      <svg className="h-4 w-4 opacity-70" viewBox="0 0 24 24" fill="none" stroke={baseColor} strokeWidth="2.5" aria-hidden="true">
        <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
      </svg>
    )
  }

  if (level === 'spotting') {
    return (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke={baseColor} strokeWidth="2" aria-hidden="true">
        <path d="M12 3C12 3 6 11 6 15A6 6 0 0 0 18 15C18 11 12 3 12 3Z" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="14" r="2.5" fill={baseColor} />
      </svg>
    )
  }

  if (level === 'light') {
    return (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke={baseColor} strokeWidth="2" aria-hidden="true">
        <path d="M12 3C12 3 6 11 6 15A6 6 0 0 0 18 15C18 11 12 3 12 3Z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )
  }

  if (level === 'medium') {
    return (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke={baseColor} strokeWidth="2" aria-hidden="true">
        <path d="M12 3C12 3 6 11 6 15A6 6 0 0 0 18 15C18 11 12 3 12 3Z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 15A6 6 0 0 0 18 15C18 13.5 15.5 12 12 12C8.5 12 6 13.5 6 15Z" fill={baseColor} />
      </svg>
    )
  }

  // heavy
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill={baseColor} stroke={baseColor} strokeWidth="2" aria-hidden="true">
      <path d="M12 3C12 3 6 11 6 15A6 6 0 0 0 18 15C18 11 12 3 12 3Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export interface FlowPickerProps {
  date: ISODate
  value: FlowLevel | undefined
  onChange: (flow: FlowLevel | undefined) => void
}

export function FlowPicker({ date, value, onChange }: FlowPickerProps) {
  return (
    <fieldset>
      <legend className="pb-2 text-sm font-medium">
        <span aria-hidden="true">{t('entry.flow')}</span>
        {/* Screen readers get the day this control applies to, spoken as a
            date rather than as an ISO string. NFR-10. */}
        <span className="sr-only">
          {t('entry.title', { date: t.date(new Date(`${date}T12:00:00`)) })}
        </span>
      </legend>

      <div className="flex gap-2" role="radiogroup" aria-label={t('entry.flow')}>
        {FLOW_LEVELS.map((level) => {
          const selected = value === level
          return (
            <button
              key={level}
              type="button"
              role="radio"
              aria-checked={selected}
              // Tapping the selected level clears it. Correcting a mistake must
              // be as cheap as making one.
              onClick={() => onChange(selected ? undefined : level)}
              className={[
                'flex min-h-11 flex-1 flex-col items-center justify-center gap-1',
                'rounded-lg border text-xs transition-all duration-200 active:scale-[0.97]',
                selected
                  ? 'border-accent bg-accent text-accent-ink shadow-sm'
                  : 'border-line hover:bg-line/40 hover:border-ink-muted/30 text-ink',
              ].join(' ')}
            >
              <FlowIcon level={level} selected={selected} />
              <span>{t(`entry.flowLevel.${level}`)}</span>
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}
